import React, { useState, useEffect } from "react"
import { Loader2 } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "src/components/ui/sheet"
import { Input } from "src/components/ui/input"
import { Button } from "src/components/ui/button"
import { FormItem, FormLabel, FormControl } from "src/components/ui/form"
import { useToast } from "src/components/ui/toast"
import { createTower, updateTower } from "../api/projectsApi"

const EMPTY = { name: "", code: "", floors_count: "" }

// Drawer to add OR edit a tower. Pass `tower` to edit; omit to create.
export function TowerFormDrawer({ open, projectId, tower, onClose, onSuccess }) {
  const { toast } = useToast()
  const [form, setForm] = useState(EMPTY)
  const [pending, setPending] = useState(false)
  const isEdit = !!tower

  useEffect(() => {
    if (!open) return
    setForm(tower
      ? { name: tower.name || "", code: tower.code || "", floors_count: tower.floors_count ?? "" }
      : EMPTY)
  }, [open, tower])

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim()) return
    setPending(true)
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || undefined,
        floors_count: form.floors_count === "" ? undefined : Number(form.floors_count),
      }
      if (isEdit) {
        await updateTower(projectId, tower.id, payload)
        toast({ title: "Tower updated", description: payload.name })
      } else {
        await createTower(projectId, payload)
        toast({ title: "Tower added", description: payload.name })
      }
      onSuccess?.()
      onClose()
    } catch (err) {
      const msg = err.response?.data?.errors?.[0] || `Could not ${isEdit ? "update" : "add"} tower`
      toast({ variant: "destructive", title: "Error", description: msg })
    } finally {
      setPending(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit tower" : "Add tower"}</SheetTitle>
          <SheetDescription>Towers group units within a project (e.g. Tower A).</SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <FormItem>
              <FormLabel>Tower name</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Tower A" value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus />
              </FormControl>
            </FormItem>
            <div className="grid grid-cols-2 gap-3">
              <FormItem>
                <FormLabel>Code</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. A" value={form.code} onChange={(e) => set("code", e.target.value)} />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormLabel>Floors</FormLabel>
                <FormControl>
                  <Input type="number" min="0" placeholder="e.g. 12" value={form.floors_count} onChange={(e) => set("floors_count", e.target.value)} />
                </FormControl>
              </FormItem>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
            <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>Cancel</Button>
            <Button type="submit" disabled={pending || !form.name.trim()} className="gap-2">
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save changes" : "Add tower"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
